import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  Optional,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';

import { InsightsEventsTrackingService } from '@core/shared/util';
import {
  FilterBadgeComponent,
  FilterClearButtonComponent,
  FilterGroupComponent,
  FilterSelectionOption,
  MultiSelectionFilterComponent,
} from '@core/ui';
import { AttributeFacet, FacetFeatureValue } from '@mp/shared/facets/domain';
import { filterBuckets, mapAttributeFacetChildrenToOptions } from '@mp/shared/facets/util';

import { AttributeFacetInsightsEventsConfig } from '../attribute-facet-insights-config';

@Component({
  selector: 'mp-attribute-facet-buckets-group',
  templateUrl: './attribute-facet-buckets-group.component.html',
  styleUrl: './attribute-facet-buckets-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FilterGroupComponent, MultiSelectionFilterComponent, FilterBadgeComponent, FilterClearButtonComponent],
})
export class AttributeFacetBucketsGroupComponent {
  readonly bucketsGroup: InputSignal<AttributeFacet.BucketLevel0> = input.required<AttributeFacet.BucketLevel0>();

  readonly selectedValues: ModelSignal<FacetFeatureValue[]> = model.required<FacetFeatureValue[]>();

  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('');

  readonly isSearchExpanded: ModelSignal<boolean> = model.required<boolean>();

  readonly isExpanded: ModelSignal<boolean> = model.required<boolean>();

  readonly icon: InputSignal<string | undefined> = input<string>();

  readonly trackInsightsEvents: InputSignal<boolean> = input<boolean>(false);

  readonly insightsEventsConfig: InputSignal<AttributeFacetInsightsEventsConfig> =
    input.required<AttributeFacetInsightsEventsConfig>();

  readonly showMoreClick: OutputEmitterRef<void> = output<void>();

  readonly searchTermChange: OutputEmitterRef<string> = output<string>();

  protected readonly searchTerm: WritableSignal<string> = signal<string>('');

  protected readonly bucketSelectedOptionsCount: Signal<number> = computed(() => this.selectedValues().length ?? 0);

  protected readonly filteredBuckets: Signal<readonly AttributeFacet.BucketLevel1<unknown>[]> = computed(() => {
    if (!this.searchTerm()) {
      return this.bucketsGroup().children;
    }

    return filterBuckets(this.bucketsGroup().children, this.searchTerm());
  });

  protected readonly visibleBucketOptions: Signal<FilterSelectionOption<FacetFeatureValue>[]> = computed(() =>
    this.getBucketOptions(this.filteredBuckets()),
  );

  constructor(@Optional() private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null) {}

  onExpandedChange(expanded: boolean): void {
    if (this.trackInsightsEvents()) {
      const insightsEvent: string = expanded
        ? this.insightsEventsConfig().filterExpand
        : this.insightsEventsConfig().filterCollapse;
      this.insightsEventsTrackingService?.trackEvent(insightsEvent);
    }
  }

  onClearSelection(): void {
    if (this.trackInsightsEvents()) {
      this.insightsEventsTrackingService?.trackEvent(this.insightsEventsConfig().filterReset);
    }

    this.searchTerm.set('');
    this.selectedValues.set([]);
  }

  onSearchTermChange(searchTerm: string): void {
    if (this.trackInsightsEvents()) {
      this.insightsEventsTrackingService?.trackEvent(this.insightsEventsConfig().groupSearch, {
        group: this.bucketsGroup().label,
      });
    }

    this.searchTerm.set(searchTerm);
    this.searchTermChange.emit(searchTerm);
  }

  onOptionSelect(option: FilterSelectionOption<FacetFeatureValue>): void {
    if (this.trackInsightsEvents()) {
      this.insightsEventsTrackingService?.trackEvent(this.insightsEventsConfig().valueSelect, {
        group: this.bucketsGroup().label,
        value: option.label,
      });
    }
  }

  private getBucketOptions(
    buckets: readonly AttributeFacet.BucketLevel1<unknown>[],
  ): FilterSelectionOption<FacetFeatureValue>[] {
    return mapAttributeFacetChildrenToOptions(buckets);
  }
}
